// Formulaire public d'un chef de pôle : il saisit son équipe en tableau (une ligne par personne), avec
// un type d'accès par personne, et — si le contact de config l'a autorisé — le cashless (règles par
// type d'accès + valeurs par personne). Aucune authentification : la seule protection est le token du
// lien. État des lignes et validations ici ; affichage dans PoleTeamTable ; logique pure dans
// poleTeamModel. Les mêmes contrôles sont refaits côté serveur (routes/accreditationPublicForm.js).
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box, Stack, Typography, Button, Alert, Chip, Dialog, DialogContent, IconButton,
} from '@mui/material';
import { Wallet, ClipboardPaste, Send, X } from 'lucide-react';
import { saveAccreditationTypeRules } from '../../api/api';
import PublicFormHeader from './PublicFormHeader';
import PoleTeamTable from './PoleTeamTable';
import PoleCashlessDrawer from './PoleCashlessDrawer';
import { eventDatesLabel } from './eventDates';
import {
  buildColumns, applyPastedRows, parsePaste, isEmptyRow, rowProblems, cashlessPayload,
} from './poleTeamModel';
import { BRAND_GRADIENT } from './formVisuals';

const INITIAL_ROWS = 6;
const MAX_PASTE_ROWS = 500;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const outlineButtonSx = {
  height: 36, px: 1.75, fontSize: 13, fontWeight: 600, color: '#002F36', whiteSpace: 'nowrap', bgcolor: '#fff',
  border: '1px solid #dde3e8', borderBottomWidth: '3px', borderRadius: '12px',
  '&:hover': { bgcolor: '#f1f5f8', borderColor: '#002F36' },
};

/** `onSubmit(people)` envoie la demande (rejette en cas d'échec) ; `onConfigChange` met à jour la config affichée. */
const PoleTeamForm = ({ token, config, onConfigChange, onSubmit }) => {
  const idRef = useRef(0);
  const makePerson = useCallback(() => ({
    id: ++idRef.current, nom: '', prenom: '', email: '', presentDays: [], customValues: {}, accessTypeKey: '',
    cashless: { credit: '', A: '', B: '', C: '', D: '' },
  }), []);

  const [people, setPeople] = useState(() => Array.from({ length: INITIAL_ROWS }, makePerson));
  const [defaultTypeKey, setDefaultTypeKey] = useState('');
  const [tried, setTried] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const accessTypes = config.accessTypes || [];
  const typeRules = config.typeRules || {};
  const customFields = config.customFields || [];
  const canCashless = Boolean(config.poleCashless);
  const hasDays = !config.fullDuration && (config.eventDays || []).length > 0;
  const columns = useMemo(() => buildColumns(config, canCashless), [config, canCashless]);

  // ---- lignes
  const patchPerson = (idx, fields) => setPeople((prev) => prev.map((p, i) => (i === idx ? { ...p, ...fields } : p)));
  const patchCustom = (idx, key, value) => setPeople((prev) => prev.map((p, i) => (
    i === idx ? { ...p, customValues: { ...p.customValues, [key]: value } } : p)));
  const patchCashless = (idx, key, value) => setPeople((prev) => prev.map((p, i) => (
    i === idx ? { ...p, cashless: { ...p.cashless, [key]: value } } : p)));
  const resetCashless = (idx) => {
    patchPerson(idx, { cashless: { credit: '', A: '', B: '', C: '', D: '' } });
    setOpenId(null);
  };
  const toggleDay = (idx, label) => setPeople((prev) => prev.map((p, i) => {
    if (i !== idx) return p;
    const has = p.presentDays.includes(label);
    return { ...p, presentDays: has ? p.presentDays.filter((d) => d !== label) : [...p.presentDays, label] };
  }));
  const addRow = () => setPeople((prev) => [...prev, makePerson()]);
  // Au moins une ligne reste affichée (supprimer la dernière la remet à blanc).
  const removeRow = (idx) => setPeople((prev) => {
    const next = prev.filter((_, i) => i !== idx);
    return next.length > 0 ? next : [makePerson()];
  });
  // Copie le type, les jours et le cashless ; identité vide pour saisir la personne suivante.
  const duplicateRow = (idx) => setPeople((prev) => {
    const src = prev[idx];
    const copy = {
      ...src, id: ++idRef.current, nom: '', prenom: '', email: '',
      presentDays: [...src.presentDays], customValues: { ...src.customValues }, cashless: { ...src.cashless },
    };
    return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
  });
  // Type par défaut : seulement les lignes déjà commencées et sans type (jamais les lignes vides).
  const applyDefaultType = () => setPeople((prev) => prev.map((p) => (
    p.accessTypeKey || isEmptyRow(p) ? p : { ...p, accessTypeKey: defaultTypeKey })));

  // ---- collage Excel
  const handlePasteRows = (idx, columnId, rows) => {
    const result = applyPastedRows({
      people, startIdx: idx, startCol: columns.findIndex((c) => c.id === columnId), rows, columns, accessTypes,
      makePerson, maxRows: MAX_PASTE_ROWS,
    });
    setPeople(result.people);
    setNotice(result.notice);
  };

  const pasteRows = pasteText.trim() ? parsePaste(pasteText) : [];
  const importPaste = () => {
    if (pasteRows.length === 0) return;
    // Les lignes vides sont remplacées : on garde les lignes remplies et on ajoute les lignes collées.
    const kept = people.filter((p) => !isEmptyRow(p));
    const result = applyPastedRows({
      people: kept, startIdx: kept.length, startCol: 0, rows: pasteRows, columns, accessTypes, makePerson, maxRows: MAX_PASTE_ROWS,
    });
    setPeople(result.people);
    setNotice(result.notice);
    setPasteOpen(false);
    setPasteText('');
  };

  // ---- règles cashless par type d'accès
  const handleSaveRules = async (rules) => {
    const { typeRules: saved } = await saveAccreditationTypeRules(token, rules);
    onConfigChange((prev) => ({ ...prev, typeRules: saved }));
  };

  // ---- validation : seules les lignes commencées comptent
  const activeIdx = people.map((p, i) => i).filter((i) => !isEmptyRow(people[i]));

  // Au-delà d'une personne, chaque email doit être renseigné, valide et distinct (compte cashless individuel).
  const emailIssues = useMemo(() => {
    const active = people.map((p, i) => i).filter((i) => !isEmptyRow(people[i]));
    if (active.length <= 1) return {};
    const seen = new Map();
    const issues = {};
    active.forEach((idx) => {
      const email = people[idx].email.trim().toLowerCase();
      if (!email || !EMAIL_RE.test(email)) { issues[idx] = 'Email valide requis'; return; }
      if (seen.has(email)) {
        issues[idx] = 'Email déjà utilisé plus haut';
        issues[seen.get(email)] = 'Email déjà utilisé plus bas';
      }
      seen.set(email, idx);
    });
    return issues;
  }, [people]);

  const incomplete = activeIdx.filter((i) => rowProblems(people[i], { customFields, emailIssue: emailIssues[i] }).length > 0).length;
  const ready = activeIdx.length - incomplete;
  const canSubmit = ready > 0 && incomplete === 0;

  const handleSubmit = async () => {
    if (!canSubmit) { setTried(true); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      await onSubmit(activeIdx.map((i) => people[i]).map((p) => ({
        nom: p.nom.trim(),
        prenom: p.prenom.trim(),
        email: p.email.trim(),
        presentDays: config.fullDuration ? [] : p.presentDays,
        customValues: p.customValues,
        accessTypeKey: p.accessTypeKey,
        ...(canCashless ? { cashless: cashlessPayload(p.cashless) } : {}),
      })));
    } catch (err) {
      setSubmitError(err?.response?.status === 404
        ? 'Ce lien de formulaire est invalide, expiré ou a été clôturé.'
        : err?.response?.data?.error || "Erreur lors de l'envoi. Réessayez dans un instant.");
      setSubmitting(false);
    }
  };

  const footerTitle = `${ready} personne${ready > 1 ? 's' : ''} prête${ready > 1 ? 's' : ''} à envoyer`;
  let footerSub = 'Les lignes vides sont ignorées';
  if (incomplete > 0) {
    footerSub = `${incomplete} ligne${incomplete > 1 ? 's' : ''} incomplète${incomplete > 1 ? 's' : ''} : nom, prénom et type d'accès requis`;
  }

  return (
    <Box sx={{
      minHeight: '100vh', height: { md: '100vh' }, display: 'flex', flexDirection: 'column', overflow: { md: 'hidden' }, bgcolor: '#EEF3F6',
    }}>
      <PublicFormHeader projectName={config.projectName} datesLabel={eventDatesLabel(config.eventDays)} />

      <Box component="main" sx={{
        flex: '1 1 auto', minHeight: 0, width: '100%', maxWidth: 1360, mx: 'auto', px: { xs: 1.5, sm: 3 }, pt: 2.25,
        display: 'flex', flexDirection: 'column', gap: 1.75,
      }}>
        <Stack direction="row" alignItems="flex-end" flexWrap="wrap" useFlexGap sx={{ gap: '12px 20px' }}>
          <Box sx={{ flex: '1 1 360px', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
              <Typography component="h1" sx={{ fontFamily: 'Switzer, Inter, sans-serif', fontSize: 24, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
                {config.label ? `Demande d'accréditations — ${config.label}` : "Demande d'accréditations"}
              </Typography>
              <Chip size="small" label="Chef de pôle" sx={{ bgcolor: '#d6f5ee', color: '#007a66', fontWeight: 600 }} />
            </Stack>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: '#5e646a', whiteSpace: 'pre-line' }}>
              {config.welcomeText
                || (canCashless
                  ? "Une ligne par personne. Le cashless suit la règle du type d'accès, sauf si vous le personnalisez."
                  : "Une ligne par personne. Choisissez un type d'accès pour chacune.")}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {canCashless && (
              <Button onClick={() => setRulesOpen(true)} startIcon={<Wallet size={15} color="#00876f" />} sx={outlineButtonSx}>
                Règles cashless
                <Chip size="small" label={accessTypes.length} sx={{ ml: 1, height: 18, fontSize: 11, fontWeight: 700, bgcolor: '#eef3f6', color: '#5e646a' }} />
              </Button>
            )}
            <Button onClick={() => setPasteOpen(true)} startIcon={<ClipboardPaste size={15} />} sx={outlineButtonSx}>
              Coller depuis Excel
            </Button>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ p: '3px', bgcolor: '#fff', border: '1px solid #dde3e8', borderRadius: '10px' }}>
              <Box
                component="select" aria-label="Type pour les lignes sans type" value={defaultTypeKey}
                onChange={(e) => setDefaultTypeKey(e.target.value)} disabled={accessTypes.length === 0}
                sx={{ height: 28, px: 0.75, fontFamily: 'inherit', fontSize: 13, color: '#002F36', bgcolor: 'transparent', border: 0, maxWidth: 210, outline: 'none' }}
              >
                <option value="">Type pour les lignes vides…</option>
                {accessTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </Box>
              <Button
                onClick={applyDefaultType} disabled={!defaultTypeKey}
                sx={{ height: 28, minWidth: 0, px: 1.25, fontSize: 12, fontWeight: 600, bgcolor: '#eef3f6', color: defaultTypeKey ? '#002F36' : '#9aa3aa', borderRadius: '7px' }}
              >
                Appliquer
              </Button>
            </Stack>
          </Stack>
        </Stack>

        {accessTypes.length === 0 && (
          <Alert severity="warning">
            Aucun type d'accès n'est encore défini pour cet événement : contactez l'organisateur avant de saisir votre équipe.
          </Alert>
        )}
        {notice && <Alert severity="info" onClose={() => setNotice('')}>{notice}</Alert>}

        <PoleTeamTable
          people={people} config={config} accessTypes={accessTypes} typeRules={typeRules} canCashless={canCashless}
          hasDays={hasDays} tried={tried} emailIssues={emailIssues} openId={openId}
          onToggleOpen={(id) => setOpenId((cur) => (cur === id ? null : id))}
          onPatch={patchPerson} onPatchCustom={patchCustom} onPatchCashless={patchCashless} onResetCashless={resetCashless}
          onToggleDay={toggleDay} onDuplicate={duplicateRow} onRemove={removeRow} onAddRow={addRow} onPasteRows={handlePasteRows}
        />

        <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 2, pt: 1.5, pb: 2 }}>
          <Box sx={{ flex: '1 1 260px' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{footerTitle}</Typography>
            <Typography sx={{ fontSize: 12, color: incomplete > 0 ? '#b54708' : '#5e646a' }}>{footerSub}</Typography>
            {submitError && <Typography sx={{ fontSize: 12, color: 'error.main' }}>{submitError}</Typography>}
          </Box>
          <Typography sx={{ fontSize: 12, color: '#9aa3aa' }}>Données transmises uniquement à l'organisateur · Propulsé par Wilout</Typography>
          <Button
            onClick={handleSubmit} disabled={submitting} startIcon={<Send size={15} />}
            sx={{
              height: 42, px: 2.75, fontSize: 14, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', background: BRAND_GRADIENT,
              borderBottom: '4px solid #00B797', borderRadius: '15px', boxShadow: '0 8px 20px -2px #00252b5c',
              opacity: canSubmit ? 1 : 0.5, '&:hover': { opacity: canSubmit ? 0.92 : 0.5 },
            }}
          >
            {submitting ? 'Envoi…' : ready > 1 ? `Envoyer ${ready} demandes` : 'Envoyer ma demande'}
          </Button>
        </Stack>
      </Box>

      {canCashless && (
        <PoleCashlessDrawer
          open={rulesOpen} onClose={() => setRulesOpen(false)} accessTypes={accessTypes} typeRules={typeRules} onSave={handleSaveRules}
        />
      )}

      <Dialog open={pasteOpen} onClose={() => setPasteOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 700 }}>Coller depuis Excel</Typography>
            <IconButton size="small" aria-label="Fermer" onClick={() => setPasteOpen(false)}><X size={18} /></IconButton>
          </Stack>
          <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: '#5e646a', mb: 1.5 }}>
            Une personne par ligne, colonnes dans cet ordre :{' '}
            <strong style={{ color: '#002F36' }}>{columns.map((c) => c.label).join(', ')}</strong>. Les lignes vides sont ignorées.
          </Typography>
          <Box
            component="textarea" rows={8} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            aria-label="Lignes à coller" placeholder={'Martin\tLéa\tlea.martin@mail.fr\tGP'}
            sx={{
              width: '100%', p: '10px 12px', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: '#002F36',
              bgcolor: '#f8fafb', border: '1px solid #dde3e8', borderRadius: '6px', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              '&:focus': { borderColor: '#00B797', bgcolor: '#fff' },
            }}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
            <Button onClick={() => setPasteOpen(false)} sx={{ color: '#5e646a', fontWeight: 600 }}>Annuler</Button>
            <Button
              onClick={importPaste} disabled={pasteRows.length === 0}
              sx={{ px: 2.25, color: '#fff', bgcolor: '#002F36', fontWeight: 600, borderRadius: '10px', '&:hover': { bgcolor: '#003d42' }, '&.Mui-disabled': { bgcolor: '#9aa3aa', color: '#fff' } }}
            >
              {pasteRows.length > 0 ? `Importer ${pasteRows.length} ligne${pasteRows.length > 1 ? 's' : ''}` : 'Importer'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PoleTeamForm;
